import {
  ActionIcon,
  Box,
  Grid,
  Group,
  List,
  ScrollArea,
  Slider,
  Switch,
  ThemeIcon,
  Text,
  LoadingOverlay,
} from '@mantine/core'
import { FC, useMemo, useState, useEffect } from 'react'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import { useResizeObserver, useValidatedState } from '@mantine/hooks'
import classes from './RouteStatistics.module.css'
import { clsx } from 'clsx'

import { getPlanningDataOptions } from '@/api/main'
import { DetailedRouteStatistics } from '@/api/main/types.gen'
import { TbCircleDot } from 'react-icons/tb'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import RouteStatisticsMap from '@/components/RouteStatisticsMap'
import { technicalSegmentsToFeatureCollection } from '@/components/RoutePlanner/geojsonBuilders'

const RouteStatistics: FC<{
  routeId: string
  routeVersion: string
  planningAreaId?: string
  routeStatistics: DetailedRouteStatistics
  selectedFragment: number
  onSelectedFragmentChange: (value: number) => void
}> = ({ routeId, routeVersion: route_version, planningAreaId, routeStatistics, selectedFragment }) => {
  const { t } = useTranslation('routeManagement')
  const [mapRef, mapRect] = useResizeObserver()
  const [displayParkingPolygons, setDisplayParkingPolygons] = useState(false)
  const [showPolygonLoader, setShowPolygonLoader] = useState(false)

  const navigate = useNavigate()
  const { route_plan, fragments, street_segments, existing_versions } = routeStatistics

  const [{ lastValidValue: highlightedSegment }, setHighlightedSegment] = useValidatedState(
    0,
    value => value >= 0 && value < route_plan.length
  )

  const planningAreaIdForQuery = planningAreaId ?? '__missing__'
  const { data: planningData, isLoading: isLoadingPlanningData } = useQuery({
    ...getPlanningDataOptions({
      path: { area_id: planningAreaIdForQuery },
      query: { include_within: 0 },
      headers: { 'city-sense-control-data-schema': 'control_data_next' },
    }),
    enabled: displayParkingPolygons && Boolean(planningAreaId),
  })

  const parkingPolygons = useMemo(
    () => (planningData ? technicalSegmentsToFeatureCollection(planningData.technical_segments) : null),
    [planningData]
  )

  useEffect(() => {
    if (displayParkingPolygons && (isLoadingPlanningData || (planningData && !parkingPolygons))) {
      const timeoutId = setTimeout(() => {
        setShowPolygonLoader(true)
      }, 300)

      return () => {
        clearTimeout(timeoutId)
      }
    } else {
      setShowPolygonLoader(false)
    }
  }, [displayParkingPolygons, isLoadingPlanningData, planningData, parkingPolygons])

  return (
    <>
      <Grid>
        <Grid.Col span={11}>
          <Group mb="xs">
            <Switch
              label={t('displayParkingPolygon')}
              checked={displayParkingPolygons}
              onChange={event => setDisplayParkingPolygons(event.currentTarget.checked)}
            />
          </Group>
          <Box pos="relative" className={classes.map} ref={mapRef}>
            <RouteStatisticsMap
              key={`${routeId}:${route_version}`}
              streetSegments={street_segments}
              routePlan={route_plan}
              fragments={fragments}
              selectedFragment={selectedFragment}
              highlightedSegment={highlightedSegment ?? 0}
              parkingPolygons={displayParkingPolygons ? parkingPolygons : null}
            />
            <LoadingOverlay
              visible={showPolygonLoader}
              zIndex={1000}
              overlayProps={{ radius: 'sm', blur: 2 }}
              loaderProps={{ children: 'Loading parking polygons...' }}
            />
          </Box>
          <Group justify="space-between">
            <Slider
              w={'calc(100% - 80px)'}
              value={highlightedSegment}
              onChange={setHighlightedSegment}
              min={0}
              max={route_plan.length - 1}
              label={index => `${index}: ${street_segments[route_plan[index].street_segment_id]!.street_name}`}
              marks={[
                {
                  value: fragments[selectedFragment]?.start_index,
                  label: t('fragmentStart'),
                },
                {
                  value: fragments[selectedFragment]?.end_index - 1,
                  label: t('fragmentEnd'),
                },
              ]}
              styles={{
                track: {
                  backgroundColor: 'var(--mantine-color-gray-6)',
                },
                markLabel: {
                  marginLeft: 40,
                  marginTop: 10,
                },
              }}
            />

            <Group gap={8}>
              <ActionIcon
                onClick={() => setHighlightedSegment(highlightedSegment! - 1)}
                disabled={highlightedSegment! <= 0}
              >
                <FaChevronLeft />
              </ActionIcon>
              <ActionIcon
                onClick={() => setHighlightedSegment(highlightedSegment! + 1)}
                disabled={highlightedSegment! + 1 >= route_plan.length}
              >
                <FaChevronRight />
              </ActionIcon>
            </Group>
          </Group>
        </Grid.Col>
        <Grid.Col span={1}>
          <Text fw={500}>{t('selectVersion')}</Text>
          <ScrollArea h={mapRect?.height ?? 790}>
            <List
              spacing="xs"
              size="sm"
              center
              icon={
                <ThemeIcon size={24} radius="xl" color="var(--mantine-color-blue-6)">
                  <TbCircleDot size={16} />
                </ThemeIcon>
              }
            >
              {[...existing_versions]
                .sort((a, b) => b.localeCompare(a))
                .map(version => (
                  <List.Item
                    key={version}
                    title={version}
                    onClick={() => {
                      navigate({ to: '/route/$routeId/$version', params: { routeId, version } })
                    }}
                    className={clsx({
                      [classes.versionItem]: true,
                      [classes.activeVersion]: version === route_version,
                    })}
                  >
                    {version}
                  </List.Item>
                ))}
            </List>
          </ScrollArea>
        </Grid.Col>
      </Grid>
    </>
  )
}

export default RouteStatistics
